import { useEffect, useMemo, useState } from 'react';
import { findProductInCatalog } from '../catalog/findProduct';
import { readRecentlyViewed, recentlyViewedStorageKey } from './recentlyViewedStorage';

/**
 * Resolve recently viewed refs to live catalog products (preserves view order).
 * @param {Record<string, import('../useProducts').Product[]>} productsByCategory
 * @param {Array<{ categorySlug: string, productId: string }>} allProductsFlat — flat list for lookup
 * @param {{ excludeCategorySlug?: string, excludeProductId?: string, limit?: number }} options
 */
export function useRecentlyViewedProducts(productsByCategory, allProductsFlat, options = {}) {
  const { excludeCategorySlug = '', excludeProductId = '', limit = 8 } = options;
  const [refs, setRefs] = useState(() => readRecentlyViewed());

  useEffect(() => {
    const refresh = () => setRefs(readRecentlyViewed());
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener('saloni-recently-viewed', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('saloni-recently-viewed', refresh);
    };
  }, []);

  return useMemo(() => {
    const flat = Array.isArray(allProductsFlat) ? allProductsFlat : [];
    const out = [];
    for (const ref of refs) {
      if (out.length >= limit) break;
      if (
        excludeCategorySlug &&
        excludeProductId &&
        ref.categorySlug === excludeCategorySlug &&
        ref.productId === excludeProductId
      ) {
        continue;
      }
      const product = findProductInCatalog(productsByCategory, ref.categorySlug, ref.productId)
        || flat.find(
          (p) =>
            String(p?.category || p?.category_slug || '') === ref.categorySlug &&
            String(p?.id || '') === ref.productId
        );
      if (product) {
        out.push({ product, categorySlug: ref.categorySlug });
      }
    }
    return out;
  }, [refs, productsByCategory, allProductsFlat, excludeCategorySlug, excludeProductId, limit]);
}

export function notifyRecentlyViewedChanged() {
  try {
    window.dispatchEvent(new Event('saloni-recently-viewed'));
  } catch {
    /* ignore */
  }
}

export { recentlyViewedStorageKey };
